import React, { useEffect, useMemo, useState } from "react";
import { ExerciseTypeContainer } from "./ExerciseTypeContainer";
import { DragSource } from "@/components/drag-source";
import { DragTarget } from "@/components/drag-target";
import { useExercise } from "../adapter/StudentExerciseContext";

const WORD_GROUP_COLORS = [
  { bg: "bg-blue-200", border: "border-blue-400" },
  { bg: "bg-green-200", border: "border-green-400" },
  { bg: "bg-purple-200", border: "border-purple-400" },
  { bg: "bg-yellow-200", border: "border-yellow-400" },
  { bg: "bg-pink-200", border: "border-pink-400" },
  { bg: "bg-orange-200", border: "border-orange-400" },
  { bg: "bg-teal-200", border: "border-teal-400" },
];

type FillInTheTextProps = {
  item: {
    id: number;
    name: string;
    description?: string;
    text?: string;
  };
  moduleId?: string;
};

export const FillInTheText: React.FC<FillInTheTextProps> = ({ item, moduleId }) => {
  const sentence = item.text || 
    "The quick brown {fox|cat|wolf} jumps over the lazy {dog|bike|chair}.";

  return (
    <ExerciseTypeContainer
      item={{
        id: String(item.id),
        type: "fill_in_the_text",
        moduleId,
      }}
      moduleId={moduleId}
      title1="Complete a frase:"
      title2={item.description || "Leia atentamente e complete a frase abaixo."}
      exerciseNumber={`exercicio n. ${item.id}`}
    >
      <FillInTheTextBody sentence={sentence} />
    </ExerciseTypeContainer>
  );
};

const FillInTheTextBody: React.FC<{
  sentence: string;
}> = ({ sentence }) => {
  const { verify, registerVerifyHandler, setCanVerify, status, evaluation } =
    useExercise();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [, setIsCorrect] = useState(false);
  const [filledWords, setFilledWords] = useState<{[key: string]: string | null}>({});
  const [feedback, setFeedback] = useState<string>("");
  const [correctAnswers, setCorrectAnswers] = useState<{[key: string]: string}>({});
  
  const handleVerify = async () => {
    // Verifica se pelo menos uma lacuna foi preenchida
    if (Object.keys(filledWords).length === 0) return;
    
    setIsVerifying(true);
    
    try {
      const result = await verify(filledWords);
      
      setIsCorrect(result.isCorrect);
      setFeedback(result.feedback || "");
      
      // Se houver uma resposta correta retornada pelo serviço, armazená-la
      if (result.correctAnswer) {
        setCorrectAnswers(result.correctAnswer);
      }
      
      setIsVerified(true);
    } catch (error) {
      console.error("Erro ao verificar resposta:", error);
      setFeedback("Ocorreu um erro ao verificar sua resposta. Tente novamente.");
      setIsCorrect(false);
      setIsVerified(true);
    } finally {
      setIsVerifying(false);
    }
  };
  
  const handleWordFilled = (gapIndex: number, word: string | null) => {
    setFilledWords(prev => ({
      ...prev,
      [gapIndex]: word
    }));
    setIsVerified(false);
    setIsCorrect(false);
    setFeedback("");
  };

  useEffect(() => {
    setCanVerify(Object.keys(filledWords).length > 0);
  }, [filledWords, setCanVerify]);

  useEffect(() => {
    registerVerifyHandler(handleVerify);
  });

  return (
    <>
      <FillTheGapsExercise 
        sentence={sentence} 
        isVerified={isVerified}
        correctAnswers={isVerified ? correctAnswers : undefined}
        onWordFilled={handleWordFilled}
        disabled={isVerifying || isVerified || status === "verifying"}
      />
      
      {(isVerified || evaluation) && feedback && (
        <div className="mt-2">
          <p className="text-sm">{feedback}</p>
        </div>
      )}
    </>
  );
};

interface FillTheGapsExerciseProps {
  sentence: string;
  isVerified?: boolean;
  correctAnswers?: {[key: string]: string};
  onWordFilled?: (gapIndex: number, word: string | null) => void;
  disabled?: boolean;
}

enum TokenType {
  Text = "text",
  Gap = "gap",
}

interface Token {
  id: number;
  type: TokenType;
  value: string;
}

function tokenizeSentence(sentence: string): Token[] {
  const tokens: Token[] = [];
  let currentToken: string = "";
  let insideBraces: boolean = false;
  let currentIndex = 0;

  for (const char of sentence) {
    if (char === "{") {
      if (currentToken) {
        tokens.push({
          id: currentIndex++,
          type: TokenType.Text,
          value: currentToken,
        });
        currentToken = "";
      }
      insideBraces = true;
    } else if (char === "}") {
      tokens.push({
        id: currentIndex++,
        type: TokenType.Gap,
        value: currentToken,
      });
      currentToken = "";
      insideBraces = false;
    } else if (char === " " && !insideBraces) {
      if (currentToken) {
        tokens.push({
          id: currentIndex++,
          type: TokenType.Text,
          value: currentToken,
        });
        currentToken = "";
      }
    } else {
      currentToken += char;
    }
  }

  if (currentToken) {
    tokens.push({
      id: currentIndex++,
      type: TokenType.Text,
      value: currentToken,
    });
  }

  return tokens;
}

interface TargetWordProps {
  token: Token;
  colorIndex: number;
  isVerified?: boolean;
  isCorrect?: boolean;
  correctAnswer?: string;
  onSelected?: (word: string | null) => void;
  disabled?: boolean;
}

const TargetWord: React.FC<TargetWordProps> = ({ 
  token, 
  colorIndex, 
  isVerified, 
  correctAnswer,
  onSelected,
  disabled = false
}) => {
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const color = WORD_GROUP_COLORS[colorIndex % WORD_GROUP_COLORS.length];

  const handleDrop = (word: unknown) => {
    if (disabled) return;
    
    const newWord = word as string;
    setSelectedWord(newWord);
    if (onSelected) {
      onSelected(newWord);
    }
  };

  const isCorrect = selectedWord === correctAnswer;

  return (
    <DragTarget
      onDrop={(data) => handleDrop(data)}
      className={`inline-block border-2 border-dotted ${color.border} mx-1 min-w-[100px] p-1 rounded ${
        isVerified ? (isCorrect ? 'bg-green-50' : 'bg-red-50') : ''
      } ${disabled ? 'opacity-70' : ''}`}
      dragId={[`item-${token?.id}`]}
    >
      {selectedWord ? (
        <span className={`${color.bg} p-1 rounded`}>{selectedWord}</span>
      ) : (
        <span className="text-gray-400">fill the blank</span>
      )}
    </DragTarget>
  );
};

const FillTheGapsExercise: React.FC<FillTheGapsExerciseProps> = ({
  sentence,
  isVerified,
  correctAnswers,
  onWordFilled,
  disabled = false
}) => {
  const [tokens, setTokens] = useState<Token[]>([]);

  useEffect(() => {
    setTokens(tokenizeSentence(sentence));
  }, [sentence]);

  const gapTokens = useMemo(() => 
    tokens.filter(token => token.type === TokenType.Gap),
    [tokens]
  );

  const components = useMemo(() => {
    let gapIndex = 0;
    return tokens.map((token) => {
      if (token.type === TokenType.Gap) {
        const currentIndex = gapIndex;
        const correctAnswer = correctAnswers ? correctAnswers[currentIndex.toString()] : undefined;
        
        const component = (
          <TargetWord 
            key={token.id} 
            token={token} 
            colorIndex={currentIndex} 
            isVerified={isVerified}
            correctAnswer={correctAnswer}
            onSelected={(word) => onWordFilled?.(currentIndex, word)}
            disabled={disabled}
          />
        );
        
        gapIndex++;
        return component;
      } else {
        return (
          <span key={token.id} className="mr-1">
            {token.value}
          </span>
        );
      }
    });
  }, [tokens, isVerified, correctAnswers, onWordFilled, disabled]);

  return (
    <div>
      <p>{components}</p>
      <div className="mt-5">
        {gapTokens.map((token, gapIndex) =>
          token.value.split("|").map((word, wordIndex) => {
            const color = WORD_GROUP_COLORS[gapIndex % WORD_GROUP_COLORS.length];
            return (
              <DragSource
                dragId={[`item-${token.id}`]}
                data={word}
                key={`${token.id}-${wordIndex}`}
                className={`${color.bg} p-1 rounded mr-2 mb-2 cursor-pointer inline-block ${
                  disabled ? 'opacity-50 pointer-events-none' : ''
                }`}
              >
                <span>{word}</span>
              </DragSource>
            );
          })
        )}
      </div>
    </div>
  );
};
